import { useState, useEffect, useCallback, useRef } from 'react';

interface UsePWADismissOptions {
  dismissKey?: string;
  dismissDuration?: number; // em milissegundos
  onDismiss?: () => void;
  onReset?: () => void;
}

export const usePWADismiss = (options: UsePWADismissOptions = {}) => {
  const {
    dismissKey = 'pwa_install_dismissed',
    dismissDuration = 60 * 60 * 1000, // 1 hora padrão
    onDismiss,
    onReset
  } = options;

  const [isDismissed, setIsDismissed] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const timerRef = useRef<number | null>(null);

  // Verifica se o aviso foi fechado recentemente
  const checkDismissedStatus = useCallback(() => {
    try {
      const dismissedTime = localStorage.getItem(dismissKey);
      if (dismissedTime) {
        const now = Date.now();
        const dismissedAt = parseInt(dismissedTime);
        const timeElapsed = now - dismissedAt;
        
        if (timeElapsed < dismissDuration) {
          setIsDismissed(true);
          setTimeRemaining(dismissDuration - timeElapsed);
          return true;
        } else {
          // Passou o tempo limite, remove o registro
          localStorage.removeItem(dismissKey);
          setIsDismissed(false);
          setTimeRemaining(0);
          return false;
        }
      }
      return false;
    } catch (error) {
      console.error('Erro ao verificar status de fechamento:', error);
      return false;
    }
  }, [dismissKey, dismissDuration]);

  // Função para fechar o aviso
  const dismiss = useCallback(() => {
    try {
      setIsDismissed(true);
      setTimeRemaining(dismissDuration);
      
      // Salva o timestamp de quando o aviso foi fechado
      localStorage.setItem(dismissKey, Date.now().toString());
      
      onDismiss?.();
    } catch (error) {
      console.error('Erro ao fechar aviso:', error);
    }
  }, [dismissKey, dismissDuration, onDismiss]);

  // Função para resetar o estado
  const reset = useCallback(() => {
    try {
      localStorage.removeItem(dismissKey);
      setIsDismissed(false);
      setTimeRemaining(0);
      
      onReset?.();
    } catch (error) {
      console.error('Erro ao resetar aviso:', error);
    }
  }, [dismissKey, onReset]);

  // Função para obter o tempo restante formatado
  const getTimeRemainingFormatted = useCallback(() => {
    if (timeRemaining <= 0) return '';
    
    const hours = Math.floor(timeRemaining / (1000 * 60 * 60));
    const minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  }, [timeRemaining]);

  // Verifica o status inicial
  useEffect(() => {
    checkDismissedStatus();
  }, [checkDismissedStatus]);

  // Timer para atualizar o tempo restante
  useEffect(() => {
    if (isDismissed && timeRemaining > 0) {
      // Limpa timer anterior se existir
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
      }
      
      // Cria novo timer
      timerRef.current = window.setInterval(() => {
        setTimeRemaining(prev => {
          const newTime = prev - 60000; // Diminui 1 minuto
          if (newTime <= 0) {
            // Tempo expirou
            setIsDismissed(false);
            localStorage.removeItem(dismissKey);
            if (timerRef.current) {
              window.clearInterval(timerRef.current);
              timerRef.current = null;
            }
            return 0;
          }
          return newTime;
        });
      }, 60000); // Verifica a cada minuto
    }

    // Cleanup do timer
    return () => {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isDismissed, timeRemaining, dismissKey]);

  return {
    isDismissed,
    timeRemaining,
    dismiss,
    reset,
    getTimeRemainingFormatted,
    checkDismissedStatus
  };
};
